import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardHeader, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertCircle, 
  CheckCircle, 
  RefreshCw,
  Download,
  Settings,
  Eye,
  Play,
  Pause,
  Zap,
  Target,
  DollarSign,
  BarChart3,
  Loader2,
  AlertTriangle,
  Sparkles,
  Brain,
  Rocket,
  Shield,
  Globe,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Filter,
  Calendar,
  ChevronRight,
  Layers,
  PieChart,
  TrendingUp as TrendIcon,
  Users,
  ShoppingCart,
  CreditCard,
  Cpu
} from 'lucide-react';

// Import sub-components
import KeywordsAudit from './components/KeywordsAudit';
import KeywordsManager from './components/KeywordsManager';
import BidOptimizer from './components/BidOptimizer';
import AlertsPanel from './components/AlertsPanel';
import RecommendationsPanel from './components/RecommendationsPanel';
import PerformanceCharts from './components/PerformanceCharts';
import AccountSelector from './components/AccountSelector';
import AuditLog from './components/AuditLog';
import HealthStatus from './components/HealthStatus';
import GoogleAuthButton from './components/GoogleAuthButton';
import AutomationPanel from './components/AutomationPanel';

// Import services
import { googleAdsService } from './services/googleAdsService';
import { useGoogleAds } from '../../hooks/useGoogleAds';

// Animated background component
const AnimatedBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-purple-950/20 to-slate-950" />
    
    {/* Animated gradient orbs */}
    <div className="absolute top-0 -left-4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob" />
    <div className="absolute top-0 -right-4 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000" />
    <div className="absolute -bottom-8 left-20 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000" />
    
    {/* Grid pattern */}
    <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]" />
    
    {/* Animated particles */}
    <div className="absolute inset-0">
      {[...Array(50)].map((_, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 bg-white/20 rounded-full animate-float"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 10}s`,
            animationDuration: `${10 + Math.random() * 20}s`
          }}
        />
      ))}
    </div>
  </div>
);

// Modern stat card component
const StatCard = ({ title, value, change, icon: Icon, color, trend }) => (
  <motion.div
    whileHover={{ scale: 1.02, y: -2 }}
    whileTap={{ scale: 0.98 }}
    className="relative group"
  >
    <div className={`
      relative overflow-hidden rounded-2xl border border-white/10 
      bg-gradient-to-br ${color} 
      backdrop-blur-xl shadow-2xl
      transition-all duration-300 group-hover:shadow-[0_0_40px_rgba(168,85,247,0.3)]
    `}>
      {/* Animated background effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
      
      <div className="relative p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="p-3 rounded-xl bg-white/10 backdrop-blur-sm">
            <Icon className="w-6 h-6 text-white" />
          </div>
          {trend && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              trend > 0 ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
            }`}>
              {trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        
        <h3 className="text-sm font-medium text-white/70 mb-1">{title}</h3>
        <p className="text-3xl font-bold text-white mb-2">{value}</p>
        
        {change && (
          <p className="text-xs text-white/50">{change}</p>
        )}
        
        {/* Progress bar */}
        <div className="mt-4 h-1 bg-white/10 rounded-full overflow-hidden">
          <motion.div 
            className="h-full bg-white/30"
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, Math.random() * 100)}%` }}
            transition={{ duration: 1, delay: 0.5 }}
          />
        </div>
      </div>
    </div>
  </motion.div>
);

// Main Dashboard Component
const GoogleAdsDashboard: React.FC = () => {
  console.log('[GoogleAdsDashboard] modules/googleAds wersja aktywna');
  
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedAccount, setSelectedAccount] = useState<string | null>('5588054537');
  const [accounts, setAccounts] = useState<any[]>([]);
  const [healthStatus, setHealthStatus] = useState<any>(null);
  const [auditData, setAuditData] = useState<any[]>([]);
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [apiStatus, setApiStatus] = useState<'online' | 'offline' | 'checking'>('checking');

  // Użyj hooka do pobierania kampanii
  const { campaigns, loading: campaignsLoading, error: campaignsError, refreshCampaigns } = useGoogleAds();

  // Kalkuluj statystyki na podstawie rzeczywistych kampanii
  const stats = React.useMemo(() => {
    console.log('🔍 Kalkulacja statystyk, kampanie:', campaigns);
    
    if (!campaigns || campaigns.length === 0) {
      console.log('⚠️ Brak kampanii do kalkulacji');
      return {
        totalBudget: 0,
        totalCost: 0,
        totalClicks: 0,
        totalImpressions: 0,
        totalConversions: 0,
        avgCtr: 0,
        avgCpc: 0
      };
    }

    const totalBudget = campaigns.reduce((sum, c) => sum + (Number(c.budget) || 0), 0);
    const totalCost = campaigns.reduce((sum, c) => sum + (Number(c.cost) || 0), 0);
    const totalClicks = campaigns.reduce((sum, c) => sum + (Number(c.clicks) || 0), 0);
    const totalImpressions = campaigns.reduce((sum, c) => sum + (Number(c.impressions) || 0), 0);
    const totalConversions = campaigns.reduce((sum, c) => sum + (Number(c.conversions) || 0), 0);
    const avgCtr = campaigns.length ? campaigns.reduce((sum, c) => sum + (Number(c.ctr) || 0), 0) / campaigns.length : 0;
    const avgCpc = campaigns.length ? campaigns.reduce((sum, c) => sum + (Number(c.avgCpc) || 0), 0) / campaigns.length : 0;

    console.log('📊 Obliczone statystyki:', { 
      totalBudget, 
      totalCost, 
      totalClicks, 
      totalImpressions, 
      totalConversions,
      avgCtr,
      avgCpc,
      campaignsCount: campaigns.length
    });

    return {
      totalBudget,
      totalCost,
      totalClicks,
      totalImpressions,
      totalConversions,
      avgCtr,
      avgCpc
    };
  }, [campaigns]);

  // Check API status on mount and log debug info
  useEffect(() => {
    console.log('🎆 GoogleAdsDashboard mounted');
    console.log('📦 Window campaigns:', (window as any).__googleAdsCampaigns);
    console.log('💾 LocalStorage campaigns:', localStorage.getItem('googleAdsCampaigns'));
    checkApiStatus();
  }, []);
  
  // Debug campaigns changes
  useEffect(() => {
    console.log('🔄 Campaigns updated in dashboard:', campaigns);
  }, [campaigns]);

  const checkApiStatus = async () => {
    try {
      setApiStatus('checking');
      const health = await googleAdsService.checkHealth('5588054537');
      
      if (health.status === 'healthy' || health.status === 'online') {
        setApiStatus('online');
        setHealthStatus(health);
        await loadInitialData();
      } else {
        setApiStatus('offline');
        setError('Serwis Google Ads API jest obecnie niedostępny. Trwa restart serwera.');
      }
    } catch (err) {
      setApiStatus('offline');
      setError('API jest w trakcie wdrażania. Spróbuj ponownie za chwilę.');
    } finally {
      setInitializing(false);
    }
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const accountsList = await googleAdsService.getAccounts();
      if (accountsList && accountsList.length > 0) {
        setAccounts(accountsList);
        setSelectedAccount(accountsList[0].customerId);
      } else {
        setAccounts([{
          customerId: '5588054537',
          descriptiveName: 'PlexiSystem - Main Account',
          currencyCode: 'PLN',
          timeZone: 'Europe/Warsaw',
          status: 'ENABLED'
        }]);
      }
    } catch (err: any) {
      console.error('Error loading initial data:', err);
      setAccounts([{
        customerId: '5588054537',
        descriptiveName: 'PlexiSystem - Main Account',
        currencyCode: 'PLN',
        timeZone: 'Europe/Warsaw',
        status: 'ENABLED'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const runAudit = async () => {
    // Jeśli nie ma selectedAccount, użyj domyślnego
    const accountId = selectedAccount || '5588054537';
    console.log('🎯 Używam konta:', accountId);
    
    if (!accountId) {
      setError('Wybierz konto Google Ads');
      return;
    }

    if (apiStatus !== 'online') {
      setError('API jest obecnie niedostępne. Spróbuj ponownie za chwilę.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Rozpoczynam audyt dla konta:', accountId);
      const auditResponse = await googleAdsService.runKeywordsAudit(accountId, {
        dateRange: {
          start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          end: new Date().toISOString().split('T')[0]
        }
      });
      
      console.log('📦 Odpowiedź z backendu:', auditResponse);
      
      // Backend zwraca { success: true, audit: [...] } lub bezpośrednio tablicę
      let auditData = [];
      if (auditResponse) {
        if (Array.isArray(auditResponse)) {
          auditData = auditResponse;
        } else if (auditResponse.audit && Array.isArray(auditResponse.audit)) {
          auditData = auditResponse.audit;
        } else if (auditResponse.success && auditResponse.audit) {
          auditData = auditResponse.audit;
        }
      }
      
      console.log('📊 Przetworzone dane audytu:', auditData.length, 'słów');
      
      if (Array.isArray(auditData) && auditData.length > 0) {
        // Mapuj dane do formatu oczekiwanego przez KeywordsAudit
        const formattedAudit = auditData.map((item: any, index: number) => ({
          keywordId: item.id || `kw_${index}`,
          text: item.keyword || item.text || '',
          campaignName: item.campaignName || 'Kampania główna',
          adGroupName: item.adGroupName || 'Grupa domyślna',
          matchType: item.matchType || 'BROAD',
          geoScope: item.geoScope || 'national',
          totalScore: item.score || item.totalScore || 0,  // POPRAWKA - używaj score z backendu!
          relevanceScore: item.relevanceScore || 0,
          intentScore: item.intentScore || 0,
          perfScore: item.performanceScore || item.perfScore || 0,
          volumeScore: item.volumeScore || 0,
          metrics: {
            clicks: item.clicks || 0,
            impressions: item.impressions || 0,
            costMicros: (item.cost || 0) * 1000000,  // Konwertuj PLN na mikro
            conversions: item.conversions || 0,
            convValue: 0,
            ctr: (item.ctr || 0) / 100,  // Backend zwraca CTR w %, konwertuj na ułamek
            cvr: item.conversionRate || 0,
            roas: item.roas || 0
          },
          intent: item.intent || 'informational'
        }));
        
        console.log('🎯 Formatted audit data:', formattedAudit.length);
        setAuditData(formattedAudit);
        
        // Statystyki audytu
        const promoted = formattedAudit.filter((k: any) => k.totalScore >= 70).length;
        const testing = formattedAudit.filter((k: any) => k.totalScore >= 40 && k.totalScore < 70).length;
        const excluded = formattedAudit.filter((k: any) => k.totalScore < 40).length;
        
        console.log('📊 Audit stats:', { 
          total: formattedAudit.length,
          promoted,
          testing,
          excluded
        });
      }
      
      // Generuj rekomendacje
      const recoResponse = await googleAdsService.generateRecommendations(accountId, {
        targetCPAbyCategory: {
          pojemniki: 120,
          gabloty: 150,
          ekspozytory: 130
        },
        localMarkets: ['Gdańsk', 'Gdynia', 'Sopot', 'Wejherowo']
      });
      
      console.log('🎯 Pełna odpowiedź rekomendacji:', recoResponse);
      
      // Backend zwraca { success: true, recommendations: [...] }
      let recoData = [];
      
      if (recoResponse && recoResponse.success && recoResponse.recommendations) {
        recoData = recoResponse.recommendations;
        console.log('✅ Znaleziono', recoData.length, 'rekomendacji z odpowiedzi');
      } else if (recoResponse && Array.isArray(recoResponse)) {
        recoData = recoResponse;
        console.log('✅ Znaleziono', recoData.length, 'rekomendacji (tablica)');
      } else if (recoResponse && recoResponse.recommendations && Array.isArray(recoResponse.recommendations)) {
        recoData = recoResponse.recommendations;
        console.log('✅ Znaleziono', recoData.length, 'rekomendacji z obiektu');
      }
      
      // Transformuj dane do formatu oczekiwanego przez frontend
      const transformedRecommendations = recoData.map((reco: any, index: number) => {
        // Określ akcję na podstawie typu lub priorytetu
        let action = 'TEST'; // domyślna akcja
        
        // Jeśli backend zwraca action.type, użyj tego
        if (reco.action && typeof reco.action === 'object' && reco.action.type) {
          const actionType = reco.action.type.toUpperCase();
          if (actionType.includes('BUDGET')) action = 'PROMOTE';
          else if (actionType.includes('OPTIMIZE')) action = 'TEST';
          else if (actionType.includes('CHECK') || actionType.includes('PAUSE')) action = 'EXCLUDE';
        }
        // Jeśli backend zwraca type bezpośrednio
        else if (reco.type) {
          if (reco.type === 'budget') action = 'PROMOTE';
          else if (reco.type === 'optimization') action = 'TEST';
          else if (reco.type === 'conversion') action = 'EXCLUDE';
        }
        // Jeśli backend zwraca priority
        else if (reco.priority) {
          if (reco.priority === 'high') action = 'PROMOTE';
          else if (reco.priority === 'medium') action = 'TEST';
          else if (reco.priority === 'low') action = 'EXCLUDE';
        }
        
        return {
          id: reco.id || `reco_${index}`,
          action: action,
          keyword: reco.campaignName ? {
            text: reco.title || reco.campaignName,
            campaignName: reco.campaignName,
            currentScore: 50 // domyślny score
          } : undefined,
          reason: reco.description || reco.title || 'Rekomendacja optymalizacji',
          changes: [
            {
              type: reco.type || 'optimization',
              description: reco.description || 'Zmiana parametrów kampanii',
              currentValue: reco.action?.value || null,
              newValue: reco.action?.value || null
            }
          ],
          riskLevel: reco.priority === 'high' ? 'low' : reco.priority === 'medium' ? 'medium' : 'high',
          expectedImpact: {
            costDeltaPct: action === 'EXCLUDE' ? -20 : action === 'PROMOTE' ? 10 : 0,
            convDeltaPct: action === 'PROMOTE' ? 50 : action === 'TEST' ? 20 : -10,
            cpaDeltaPct: action === 'PROMOTE' ? -30 : action === 'TEST' ? -10 : 20
          },
          confidence: reco.priority === 'high' ? 90 : reco.priority === 'medium' ? 70 : 50,
          estimatedImpact: reco.estimatedImpact || '+20% wydajności'
        };
      });
      
      console.log('🎯 Przetransformowane rekomendacje:', transformedRecommendations);
      setRecommendations(transformedRecommendations);
      
    } catch (err: any) {
      console.error('Audit error:', err);
      setError(err.message || 'Błąd podczas audytu. Sprawdź czy API jest dostępne.');
    } finally {
      setLoading(false);
    }
  };

  const calculatePotentialSavings = (audit: any[]) => {
    const lowPerformers = audit.filter(k => k.totalScore < 40);
    const totalWaste = lowPerformers.reduce((acc, k) => acc + (k.metrics?.costMicros || 0), 0);
    return Math.round(totalWaste / 1000000);
  };

  const calculateExpectedConversions = (audit: any[]) => {
    const highPerformers = audit.filter(k => k.totalScore >= 70);
    if (highPerformers.length === 0) return 0;
    const avgCvr = highPerformers.reduce((acc, k) => acc + (k.metrics?.cvr || 0), 0) / highPerformers.length;
    return Math.round(highPerformers.length * avgCvr * 1.2);
  };

  // Show loading screen while initializing
  if (initializing) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <AnimatedBackground />
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="relative">
            <div className="absolute inset-0 animate-ping">
              <Cpu className="w-16 h-16 text-purple-500/30" />
            </div>
            <Cpu className="w-16 h-16 text-purple-500 mx-auto mb-4 animate-pulse" />
          </div>
          <p className="text-white/70 font-medium">Inicjalizacja Google Ads AI...</p>
          <p className="text-white/40 text-sm mt-2">Łączenie z API</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <AnimatedBackground />
      
      <div className="relative z-10 max-w-[1600px] mx-auto p-6 space-y-6">
        {/* Modern Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-purple-900/30 via-pink-900/20 to-blue-900/30 backdrop-blur-2xl border border-white/10 p-8"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-transparent to-pink-500/10 animate-gradient" />
          
          <div className="relative flex justify-between items-center">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 shadow-lg shadow-purple-500/30">
                  <Brain className="w-8 h-8 text-white" />
                </div>
                <div className="flex items-center gap-2">
                  <h1 className="text-4xl font-bold bg-gradient-to-r from-white via-purple-200 to-pink-200 bg-clip-text text-transparent">
                    Google Ads AI
                  </h1>
                  <span className="px-3 py-1 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-xs font-medium text-purple-300">
                    v2.0 Neural
                  </span>
                </div>
              </div>
              <p className="text-white/60">Inteligentna optymalizacja kampanii z uczeniem maszynowym</p>
            </div>
            
            <div className="flex gap-3">
              <GoogleAuthButton variant="compact" />
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={async () => {
                  setLoading(true);
                  try {
                    // Użyj funkcji z hooka
                    await refreshCampaigns();
                    console.log('📊 Odświeżono kampanie');
                  } catch (err) {
                    console.error('Błąd odświeżania:', err);
                    setError('Nie udało się odświeżyć danych');
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={campaignsLoading}
                className="px-6 py-3 rounded-2xl font-medium bg-blue-600/20 backdrop-blur-sm border border-blue-500/30 hover:bg-blue-600/30 transition-all duration-300 flex items-center gap-2"
              >
                <RefreshCw className={`w-5 h-5 ${loading || campaignsLoading ? 'animate-spin' : ''}`} />
                Odśwież dane
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  console.log('🔥 KLIKNIĘTO PRZYCISK AUDYT!');
                  console.log('API Status:', apiStatus);
                  console.log('Loading:', loading);
                  console.log('Selected Account:', selectedAccount);
                  if (apiStatus !== 'online') {
                    console.error('API jest offline!');
                    alert('API jest offline! Status: ' + apiStatus);
                  } else {
                    runAudit();
                  }
                }}
                disabled={loading || apiStatus !== 'online'}
                className={`
                  relative group px-6 py-3 rounded-2xl font-medium
                  bg-gradient-to-r from-purple-600 to-pink-600 
                  text-white shadow-lg shadow-purple-500/30
                  hover:shadow-xl hover:shadow-purple-500/40
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-all duration-300
                `}
              >
                <span className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 blur opacity-50 group-hover:opacity-75 transition-opacity" />
                <span className="relative flex items-center gap-2">
                  {loading ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Analizuję...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      Uruchom AI Audyt
                    </>
                  )}
                </span>
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 rounded-2xl font-medium bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all duration-300"
                onClick={() => window.location.href = '/api-settings'}
                title="Ustawienia AI dla Google Ads"
              >
                <Settings className="w-5 h-5" />
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* API Status Bar */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10"
        >
          <div className="flex items-center gap-3">
            <div className={`relative ${apiStatus === 'checking' ? 'animate-pulse' : ''}`}>
              <div className={`w-3 h-3 rounded-full ${
                apiStatus === 'online' ? 'bg-green-500' : 
                apiStatus === 'offline' ? 'bg-red-500' : 
                'bg-yellow-500'
              }`} />
              <div className={`absolute inset-0 w-3 h-3 rounded-full animate-ping ${
                apiStatus === 'online' ? 'bg-green-500' : 
                apiStatus === 'offline' ? 'bg-red-500' : 
                'bg-yellow-500'
              }`} />
            </div>
            <span className="text-sm font-medium text-white/70">
              Status API: {
                apiStatus === 'online' ? 'Online' : 
                apiStatus === 'offline' ? 'Offline' : 
                'Sprawdzanie...'
              }
            </span>
          </div>
          
          {selectedAccount && (
            <div className="flex items-center gap-2 ml-auto">
              <Globe className="w-4 h-4 text-white/40" />
              <span className="text-sm text-white/60">Konto: {selectedAccount}</span>
            </div>
          )}
        </motion.div>

        {/* Main Stats Grid - Rzeczywiste dane z kampanii - CIEMNE TŁO */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Budżet dzienny"
            value={`${stats.totalBudget.toFixed(2)} PLN`}
            change={`${campaigns.length} aktywnych kampanii`}
            icon={CreditCard}
            color="from-slate-900/80 to-purple-900/60"
            trend={campaigns.length > 0 ? 12 : 0}
          />
          <StatCard
            title="Wyświetlenia"
            value={stats.totalImpressions.toLocaleString('pl-PL')}
            change={`CTR: ${stats.avgCtr.toFixed(2)}%`}
            icon={Eye}
            color="from-slate-900/80 to-green-900/60"
            trend={stats.avgCtr > 3 ? 8 : -5}
          />
          <StatCard
            title="Kliknięcia"
            value={stats.totalClicks.toLocaleString('pl-PL')}
            change={`Śr. CPC: ${stats.avgCpc.toFixed(2)} PLN`}
            icon={Target}
            color="from-slate-900/80 to-blue-900/60"
            trend={stats.totalClicks > 100 ? 15 : -10}
          />
          <StatCard
            title="Konwersje"
            value={stats.totalConversions}
            change={`Koszt: ${stats.totalCost.toFixed(2)} PLN`}
            icon={ShoppingCart}
            color="from-slate-900/80 to-pink-900/60"
            trend={stats.totalConversions > 20 ? 25 : 0}
          />
        </div>

        {/* Modern Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="w-full p-1 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl">
            {['overview', 'keywords', 'optimizer', 'audit', 'recommendations', 'automation', 'alerts', 'performance', 'history'].map((tab) => (
              <TabsTrigger
                key={tab}
                value={tab}
                className="flex-1 py-3 rounded-xl font-medium transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-pink-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-purple-500/30"
              >
                {tab === 'overview' && 'Przegląd'}
                {tab === 'keywords' && 'Słowa kluczowe'}
                {tab === 'optimizer' && 'Optymalizator'}
                {tab === 'audit' && 'Audyt AI'}
                {tab === 'recommendations' && 'Rekomendacje'}
                {tab === 'automation' && 'Automatyzacja'}
                {tab === 'alerts' && 'Alerty'}
                {tab === 'performance' && 'Wydajność'}
                {tab === 'history' && 'Historia'}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Tab Contents */}
          <AnimatePresence mode="wait">
            {activeTab === 'overview' && (
              <TabsContent value="overview" className="mt-0">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-6"
                >
                  {/* Quick Actions - DZIAŁAJĄCE PRZYCISKI */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { 
                        title: 'Wdróż TOP 10', 
                        icon: Rocket, 
                        color: 'from-green-600 to-emerald-600',
                        desc: 'Bezpieczne rekomendacje',
                        action: async () => {
                          try {
                            setLoading(true);
                            const result = await googleAdsService.implementTop10Keywords();
                            alert(`Wdrożono ${result.implemented} słów kluczowych!`);
                            await runAudit();
                          } catch (error) {
                            console.error('Error:', error);
                            setError('Nie udało się wdrożyć rekomendacji');
                          } finally {
                            setLoading(false);
                          }
                        }
                      },
                      { 
                        title: 'Testy A/B', 
                        icon: Activity, 
                        color: 'from-yellow-600 to-amber-600',
                        desc: 'Średni performerzy',
                        action: async () => {
                          try {
                            setLoading(true);
                            // Wywołaj funkcję testów A/B
                            const campaignId = campaigns && campaigns.length > 0 ? campaigns[0].id : '21477685170';
                            const result = await googleAdsService.startABTest(campaignId, {
                              controlVariant: 'current',
                              testVariant: 'optimized',
                              metric: 'ctr',
                              duration: 14
                            });
                            alert(`Rozpoczęto test A/B dla kampanii!`);
                            await runAudit();
                          } catch (error) {
                            console.error('Error:', error);
                            alert('Funkcja testów A/B jest w trakcie implementacji');
                          } finally {
                            setLoading(false);
                          }
                        }
                      },
                      { 
                        title: 'Wyklucz słabe', 
                        icon: Shield, 
                        color: 'from-red-600 to-rose-600',
                        desc: 'Wynik < 20',
                        action: async () => {
                          try {
                            setLoading(true);
                            const result = await googleAdsService.excludeWeakKeywords();
                            alert(`Wykluczono ${result.excluded} słabych słów kluczowych!`);
                            await runAudit();
                          } catch (error) {
                            console.error('Error:', error);
                            setError('Nie udało się wykluczyć słów');
                          } finally {
                            setLoading(false);
                          }
                        }
                      },
                      { 
                        title: 'Eksport CSV', 
                        icon: Download, 
                        color: 'from-purple-600 to-pink-600',
                        desc: 'Pełny raport',
                        action: async () => {
                          try {
                            setLoading(true);
                            const blob = await googleAdsService.exportCampaigns(
                              campaigns.map(c => c.id),
                              'csv'
                            );
                            // Pobierz plik
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `google-ads-export-${new Date().toISOString().split('T')[0]}.csv`;
                            a.click();
                            window.URL.revokeObjectURL(url);
                          } catch (error) {
                            console.error('Error:', error);
                            setError('Nie udało się wyeksportować danych');
                          } finally {
                            setLoading(false);
                          }
                        }
                      }
                    ].map((action, i) => (
                      <motion.button
                        key={i}
                        whileHover={{ scale: 1.03, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={action.action}
                        disabled={loading}
                        className="relative group p-6 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 hover:bg-white/10 transition-all duration-300 disabled:opacity-50"
                      >
                        <div className={`p-3 rounded-xl bg-gradient-to-r ${action.color} w-fit mb-4`}>
                          <action.icon className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="font-semibold text-white mb-1">{action.title}</h3>
                        <p className="text-sm text-white/50">{action.desc}</p>
                        <ChevronRight className="absolute top-6 right-6 w-5 h-5 text-white/30 group-hover:text-white/60 transition-colors" />
                      </motion.button>
                    ))}
                  </div>

                  {/* Performance Overview */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="p-6 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <PieChart className="w-5 h-5 text-purple-400" />
                        Statystyki kampanii
                      </h3>
                      <div className="space-y-3">
                        {[
                          { label: 'Wyświetlenia', value: stats.totalImpressions.toLocaleString('pl-PL'), color: 'bg-blue-500' },
                          { label: 'Kliknięcia', value: stats.totalClicks.toLocaleString('pl-PL'), color: 'bg-green-500' },
                          { label: 'Konwersje', value: stats.totalConversions, color: 'bg-purple-500' },
                          { label: 'Śr. CTR', value: `${stats.avgCtr.toFixed(2)}%`, color: 'bg-yellow-500' },
                          { label: 'Śr. CPC', value: `${stats.avgCpc.toFixed(2)} PLN`, color: 'bg-pink-500' }
                        ].map((item, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${item.color}`} />
                            <span className="text-sm text-white/70 flex-1">{item.label}</span>
                            <span className="text-sm font-medium">{item.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="p-6 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10">
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-purple-400" />
                        Lista kampanii
                      </h3>
                      <div className="space-y-3">
                        {campaigns && campaigns.length > 0 ? (
                          campaigns.slice(0, 5).map((campaign) => (
                            <div key={campaign.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                              <div className="flex-1">
                                <div className="font-medium text-sm">{campaign.name}</div>
                                <div className="text-xs text-white/50 mt-1">
                                  Status: <span className={campaign.status === 'ENABLED' || campaign.status === 'enabled' || campaign.status === 'active' ? 'text-green-400' : 'text-red-400'}>
                                    {campaign.status === 'ENABLED' || campaign.status === 'enabled' || campaign.status === 'active' ? 'Aktywna' : 'Wstrzymana'}
                                  </span>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-medium">{Number(campaign.budget).toFixed(2)} PLN/dzień</div>
                                <div className="text-xs text-white/50">CTR: {Number(campaign.ctr).toFixed(2)}%</div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-8 text-white/40">
                            <p className="text-sm">Brak kampanii do wyświetlenia</p>
                            <button 
                              onClick={refreshCampaigns}
                              className="mt-2 text-purple-400 hover:text-purple-300 text-xs underline"
                            >
                              Odśwież dane
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </TabsContent>
            )}
          </AnimatePresence>

          {/* Keywords tab */}
          <TabsContent value="keywords" className="mt-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 p-6"
            >
              {campaigns && campaigns.length > 0 ? (
                <KeywordsManager 
                  campaignId={campaigns[0].id}
                  campaignName={campaigns[0].name}
                  onRefresh={refreshCampaigns}
                />
              ) : (
                <div className="text-center py-12">
                  <Target className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/60">Wybierz kampanię, aby zarządzać słowami kluczowymi</p>
                  <button 
                    onClick={refreshCampaigns}
                    className="mt-4 px-4 py-2 rounded-lg bg-purple-600/20 border border-purple-500/30 text-purple-400 hover:bg-purple-600/30 transition-colors"
                  >
                    Odśwież kampanie
                  </button>
                </div>
              )}
            </motion.div>
          </TabsContent>

          {/* Optimizer tab */}
          <TabsContent value="optimizer" className="mt-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 p-6"
            >
              {campaigns && campaigns.length > 0 ? (
                <BidOptimizer 
                  campaignId={campaigns[0].id}
                  onApply={(strategy) => {
                    console.log('Applied strategy:', strategy);
                    // TODO: Implement strategy application
                  }}
                />
              ) : (
                <div className="text-center py-12">
                  <Zap className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/60">Wybierz kampanię do optymalizacji</p>
                </div>
              )}
            </motion.div>
          </TabsContent>

          {/* Other tabs */}
          <TabsContent value="audit" className="mt-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 p-6"
            >
              <KeywordsAudit 
                data={auditData} 
                loading={loading}
                onRefresh={runAudit}
              />
            </motion.div>
          </TabsContent>

          <TabsContent value="recommendations" className="mt-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 p-6"
            >
              {recommendations.length === 0 && !loading ? (
                <div className="text-center py-12">
                  <Sparkles className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/60 mb-4">Brak rekomendacji do wyświetlenia</p>
                  <p className="text-white/40 text-sm mb-6">Uruchom AI Audyt aby wygenerować rekomendacje optymalizacji</p>
                  <button
                    onClick={runAudit}
                    disabled={loading || apiStatus !== 'online'}
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium hover:shadow-lg transition-all disabled:opacity-50"
                  >
                    <Sparkles className="w-5 h-5 inline mr-2" />
                    Uruchom AI Audyt
                  </button>
                </div>
              ) : (
                <RecommendationsPanel 
                  recommendations={recommendations}
                  loading={loading}
                  onApply={async (recoIds, dryRun) => {
                    console.log('🎯 Wdrażanie rekomendacji:', recoIds, 'Dry run:', dryRun);
                    
                    try {
                      // Znajdź wybrane rekomendacje
                      const selectedRecommendations = recommendations.filter(r => recoIds.includes(r.id));
                      
                      const response = await fetch('https://api.plexisystem.pl/api/google-ads/recommendations/apply', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                          recommendations: selectedRecommendations,
                          dryRun: dryRun
                        })
                      });
                      
                      if (!response.ok) {
                        throw new Error('Błąd wdrażania rekomendacji');
                      }
                      
                      const result = await response.json();
                      
                      if (dryRun) {
                        console.log('👁️ Podgląd zmian:', result.preview);
                      } else {
                        console.log('✅ Wdrożono rekomendacje:', result);
                        
                        // Odśwież dane po wdrożeniu
                        await refreshCampaigns();
                        await runAudit();
                        
                        // Pokaż komunikat sukcesu
                        alert(`✅ Wdrożono ${result.applied} z ${result.total} rekomendacji!`);
                      }
                      
                      return result;
                    } catch (error) {
                      console.error('❌ Błąd wdrażania rekomendacji:', error);
                      alert('Wystąpił błąd podczas wdrażania rekomendacji');
                      throw error;
                    }
                  }}
                />
              )}
            </motion.div>
          </TabsContent>

          <TabsContent value="automation" className="mt-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 p-6"
            >
              <AutomationPanel />
            </motion.div>
          </TabsContent>

          <TabsContent value="alerts" className="mt-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 p-6"
            >
              <AlertsPanel 
                campaignId={campaigns && campaigns.length > 0 ? campaigns[0].id : undefined}
              />
            </motion.div>
          </TabsContent>

          <TabsContent value="performance" className="mt-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 p-6"
            >
              <PerformanceCharts 
                data={auditData}
                loading={loading}
              />
            </motion.div>
          </TabsContent>

          <TabsContent value="history" className="mt-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 p-6"
            >
              <AuditLog />
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Custom styles */}
      <style jsx>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes gradient {
          0%, 100% { transform: translateX(-100%); }
          50% { transform: translateX(100%); }
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        .animate-float {
          animation: float linear infinite;
        }
        
        .animate-gradient {
          animation: gradient 8s ease infinite;
        }
      `}</style>
    </div>
  );
};

export default GoogleAdsDashboard;
